import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Header from "@/components/Header";
import { IndicativeBadge } from "@/components/IndicativeBadge";
import { USDAOfficialPrices } from "@/components/USDAOfficialPrices";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import GlobalPriceHistoryModal from "@/components/GlobalPriceHistoryModal";
import GlobalAlertModal from "@/components/GlobalAlertModal";
import {
  CaretUp, CaretDown, Minus, MagnifyingGlass, ArrowsDownUp, CircleNotch,
  Trophy, Medal, Factory, Storefront, MapTrifold, FlagBanner, ChartLine, Bell, PushPin, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function USAMarketsPage() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState("packers");
  
  const [packers, setPackers] = useState([]);
  const [packersLoading, setPackersLoading] = useState(true);
  const [packerRegion, setPackerRegion] = useState("");
  const [packerState, setPackerState] = useState("");
  const [packerSortBy, setPackerSortBy] = useState("price_desc");
  
  const [auctions, setAuctions] = useState([]);
  const [auctionStats, setAuctionStats] = useState(null);
  const [auctionsLoading, setAuctionsLoading] = useState(true);
  const [auctionRegion, setAuctionRegion] = useState("");
  const [auctionState, setAuctionState] = useState("");
  const [auctionCategory, setAuctionCategory] = useState("feeder_steers_500_600lb");
  const [auctionSortBy, setAuctionSortBy] = useState("price_desc");
  const [searchQuery, setSearchQuery] = useState("");
  
  const [states, setStates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  
  // Modal states
  const [historyModal, setHistoryModal] = useState({ open: false, market: null, category: null, price: null });
  const [alertModal, setAlertModal] = useState({ open: false, market: null, price: null, category: null });

  const usRegions = ["Midwest", "South", "West"];

  const fetchStates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/usa/states`);
      setStates(response.data.states);
    } catch (err) {
      console.error("Failed to fetch states:", err);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/usa/categories`);
      setCategories(response.data.categories);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  }, []);

  const fetchWatchlist = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/watchlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWatchlist(response.data);
    } catch (err) {
      console.error("Failed to fetch watchlist:", err);
    }
  }, [token]);

  const fetchPackers = useCallback(async () => {
    setPackersLoading(true);
    try {
      const response = await axios.get(`${API}/usa/packers`, {
        params: {
          region: packerRegion || undefined,
          state: packerState || undefined,
          sort_by: packerSortBy
        }
      });
      setPackers(response.data.packers);
      setLastUpdated(response.data.last_updated);
    } catch (error) {
      toast.error("Failed to load USA packer prices");
    } finally {
      setPackersLoading(false);
    }
  }, [packerRegion, packerState, packerSortBy]);

  const fetchAuctions = useCallback(async () => {
    setAuctionsLoading(true);
    try {
      const response = await axios.get(`${API}/usa/auctions`, {
        params: {
          region: auctionRegion || undefined,
          state: auctionState || undefined,
          category: auctionCategory,
          sort_by: auctionSortBy
        }
      });
      setAuctions(response.data.auctions);
      setAuctionStats(response.data.stats);
      setLastUpdated(response.data.last_updated);
    } catch (error) {
      toast.error("Failed to load USA auction prices");
    } finally {
      setAuctionsLoading(false);
    }
  }, [auctionRegion, auctionState, auctionCategory, auctionSortBy]);

  useEffect(() => {
    fetchStates();
    fetchCategories();
    if (token) fetchWatchlist();
  }, [token, fetchStates, fetchCategories, fetchWatchlist]);

  useEffect(() => {
    if (activeTab === "packers") {
      fetchPackers();
    } else {
      fetchAuctions();
    }
  }, [activeTab, fetchPackers, fetchAuctions]);

  const toggleWatchlist = async (marketName, marketType, category = null) => {
    if (!token) {
      toast.error("Please sign in to use watchlist");
      return;
    }

    const isInWL = watchlist.some(w => 
      w.market_name === marketName && w.country === "usa" && w.category === category
    );

    try {
      if (isInWL) {
        const item = watchlist.find(w => 
          w.market_name === marketName && w.country === "usa" && w.category === category
        );
        await axios.delete(`${API}/user/watchlist/${item.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Removed from watchlist");
      } else {
        await axios.post(`${API}/user/watchlist`, {
          country: "usa",
          market_type: marketType,
          market_name: marketName,
          category
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Added to watchlist");
      }
      fetchWatchlist();
    } catch (error) {
      toast.error("Failed to update watchlist");
    }
  };

  const isInWatchlist = (marketName, category = null) => {
    return watchlist.some(w => 
      w.market_name === marketName && w.country === "usa" && w.category === category
    );
  };

  const formatPrice = (price, unit) => {
    if (!price) return "N/A";
    if (unit === "$/head") return `$${price.toLocaleString()}`;
    return `$${price.toFixed(2)}/cwt`;
  };

  const getChangeColor = (change) => change > 0 ? "text-[#059669]" : change < 0 ? "text-[#DC2626]" : "text-[#6B6661]";
  const getChangeBg = (change) => change > 0 ? "bg-[#ECFDF5]" : change < 0 ? "bg-[#FEF2F2]" : "bg-[#F6E9DD]";

  const getStatesInRegion = (region) => {
    if (!region) return states;
    return states.filter(s => s.region === region);
  };

  const filteredPackers = packers.filter(p =>
    p.packer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.state.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredAuctions = auctions.filter(a =>
    a.auction_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.county.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.state.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentCategory = categories.find(c => c.id === auctionCategory);

  const getRankBadge = (idx) => {
    if (idx === 0) return <Trophy size={18} className="text-[#FCD34D]" weight="fill" />;
    if (idx === 1) return <Medal size={18} className="text-[#8B8580]" weight="fill" />;
    if (idx === 2) return <Medal size={18} className="text-[#B45309]" weight="fill" />;
    return <span className="text-[#6B6661] font-mono text-sm">{idx + 1}</span>;
  };

  const getRegionColor = (region) => {
    switch (region) {
      case "Midwest": return "text-[#60A5FA]";
      case "South": return "text-[#F97316]";
      case "West": return "text-[#A78BFA]";
      default: return "text-[#8B8580]";
    }
  };

  return (
    <div className="min-h-screen bg-[#022C22]">
      <SEO title="USA Cattle Market Prices" description="Live US cattle prices from packers and auction barns. Compare fed cattle, feeder steers and heifer prices across all major US regions." url="/usa-markets" />
      <Header />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2 py-1 bg-[#DC2626] text-white text-xs font-mono uppercase tracking-wider">USA Markets</span>
            <span className="px-2 py-1 bg-[#FEF3C7] text-[#92400E] text-xs font-mono uppercase tracking-wider">
              {activeTab === "packers" ? `${filteredPackers.length} Packers` : `${filteredAuctions.length} Auctions`}
            </span>
            <span className="px-2 py-1 bg-[#065F46] text-white text-xs font-mono flex items-center gap-1">
              <Clock size={12} /> 15min updates
            </span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-white mb-2">United States Livestock Markets</h1>
          <div className="mb-2"><IndicativeBadge /></div>
          <p className="text-[#8B8580] font-sans">Indicative prices for beef packers and livestock auctions across America. <span className="text-[#6B6661]">Prices in USD.</span></p>
        </div>

        {/* Official USDA feed (activates when key configured) */}
        <USDAOfficialPrices />

        <div className="flex items-center gap-2 mb-6">
          <button onClick={() => setActiveTab("packers")} data-testid="tab-packers"
            className={`px-4 py-2 font-sans text-sm transition-colors ${activeTab === "packers" ? "bg-[#DC2626] text-white" : "bg-[#065F46] text-white/70 hover:text-white"}`}>
            <Factory size={16} className="inline mr-2" />Packer Prices
          </button>
          <button onClick={() => setActiveTab("auctions")} data-testid="tab-auctions"
            className={`px-4 py-2 font-sans text-sm transition-colors ${activeTab === "auctions" ? "bg-[#DC2626] text-white" : "bg-[#065F46] text-white/70 hover:text-white"}`}>
            <Storefront size={16} className="inline mr-2" />Livestock Auctions
          </button>
        </div>

        {activeTab === "packers" && (
          <>
            <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-[#064E3B] border border-[#065F46]">
              <div className="flex items-center gap-2">
                <MapTrifold size={18} className="text-[#DC2626]" />
                <select value={packerRegion} onChange={(e) => { setPackerRegion(e.target.value); setPackerState(""); }} data-testid="packer-region-filter"
                  className="px-3 py-2 bg-[#065F46] border border-[#0F766E] text-white font-sans text-sm focus:outline-none focus:border-[#DC2626]">
                  <option value="">All Regions</option>
                  {usRegions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <FlagBanner size={18} className="text-[#DC2626]" />
                <select value={packerState} onChange={(e) => setPackerState(e.target.value)} data-testid="packer-state-filter"
                  className="px-3 py-2 bg-[#065F46] border border-[#0F766E] text-white font-sans text-sm focus:outline-none focus:border-[#DC2626]">
                  <option value="">All States</option>
                  {getStatesInRegion(packerRegion).map(s => <option key={s.name} value={s.name}>{s.name} ({s.auction_count})</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <ArrowsDownUp size={18} className="text-[#DC2626]" />
                <select value={packerSortBy} onChange={(e) => setPackerSortBy(e.target.value)} data-testid="packer-sort-filter"
                  className="px-3 py-2 bg-[#065F46] border border-[#0F766E] text-white font-sans text-sm focus:outline-none focus:border-[#DC2626]">
                  <option value="price_desc">Highest Price</option>
                  <option value="price_asc">Lowest Price</option>
                  <option value="name">Name (A-Z)</option>
                </select>
              </div>
              <div className="relative flex-1 min-w-[200px]">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
                <input type="text" placeholder="Search packers..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input" className="w-full pl-9 pr-4 py-2 bg-[#065F46] border border-[#0F766E] text-white placeholder-white/50 font-sans text-sm focus:outline-none focus:border-[#DC2626]" />
              </div>
            </div>

            <div className="bg-white border border-[#E0D4C8] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="packers-table">
                  <thead className="bg-[#F6E9DD]">
                    <tr>
                      <th className="text-center px-3 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661] w-12">#</th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">Packer</th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">Location</th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">Region</th>
                      <th className="text-center px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">Grade</th>
                      <th className="text-right px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">Price</th>
                      <th className="text-right px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">15min</th>
                      <th className="text-center px-2 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {packersLoading ? (
                      <tr><td colSpan={8} className="px-4 py-12 text-center">
                        <CircleNotch size={32} className="animate-spin text-[#DC2626] mx-auto mb-2" />
                        <span className="text-[#6B6661] text-sm">Loading packer prices...</span>
                      </td></tr>
                    ) : filteredPackers.length === 0 ? (
                      <tr><td colSpan={8} className="px-4 py-12 text-center text-[#6B6661]">No packers found.</td></tr>
                    ) : filteredPackers.map((packer, idx) => (
                      <tr key={packer.id} className="hover:bg-[#F9FAFB] transition-colors" data-testid={`packer-row-${idx}`}>
                        <td className="px-3 py-3 text-center">{getRankBadge(idx)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Factory size={16} className="text-[#DC2626]" />
                            <span className="text-[#33302E] text-sm">{packer.packer_name}</span>
                            {isInWatchlist(packer.packer_name) && <PushPin size={14} className="text-[#F59E0B]" weight="fill" />}
                          </div>
                        </td>
                        <td className="px-4 py-3"><span className="text-[#6B6661] text-sm">{packer.location}</span></td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 bg-[#E5E7EB] text-xs font-mono ${getRegionColor(packer.region)}`}>{packer.region}</span></td>
                        <td className="px-4 py-3 text-center"><span className="px-2 py-0.5 bg-[#FCD34D]/20 text-[#92400E] text-xs font-mono">{packer.grade}</span></td>
                        <td className="px-4 py-3 text-right"><span className="font-mono text-sm text-[#33302E]">{formatPrice(packer.price, packer.unit)}</span></td>
                        <td className="px-4 py-3 text-right">
                          <div className={`inline-flex items-center gap-1 px-2 py-0.5 ${getChangeBg(packer.change)}`}>
                            {packer.change > 0 ? <CaretUp size={12} className="text-[#059669]" weight="fill" /> : packer.change < 0 ? <CaretDown size={12} className="text-[#DC2626]" weight="fill" /> : <Minus size={12} className="text-[#6B6661]" />}
                            <span className={`font-mono text-sm ${getChangeColor(packer.change_percent)}`}>{packer.change_percent >= 0 ? "+" : ""}{packer.change_percent.toFixed(1)}%</span>
                          </div>
                        </td>
                        <td className="px-2 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => setHistoryModal({ open: true, market: packer.packer_name, category: null, price: packer.price })}
                              className="p-1.5 hover:bg-[#E5E7EB] transition-colors" title="View History">
                              <ChartLine size={16} className="text-[#0F766E]" />
                            </button>
                            <button onClick={() => setAlertModal({ open: true, market: packer.packer_name, price: packer.price, category: null, marketType: "packer" })}
                              className="p-1.5 hover:bg-[#E5E7EB] transition-colors" title="Set Alert">
                              <Bell size={16} className="text-[#F59E0B]" />
                            </button>
                            <button onClick={() => toggleWatchlist(packer.packer_name, "packer")}
                              className="p-1.5 hover:bg-[#E5E7EB] transition-colors" title="Add to Watchlist">
                              <PushPin size={16} className={isInWatchlist(packer.packer_name) ? "text-[#F59E0B]" : "text-[#6B6661]"} weight={isInWatchlist(packer.packer_name) ? "fill" : "regular"} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === "auctions" && (
          <>
            <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-[#064E3B] border border-[#065F46]">
              <select value={auctionCategory} onChange={(e) => setAuctionCategory(e.target.value)} data-testid="category-filter"
                className="px-3 py-2 bg-[#065F46] border border-[#0F766E] text-white font-sans text-sm focus:outline-none focus:border-[#DC2626]">
                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
              </select>
              <select value={auctionRegion} onChange={(e) => { setAuctionRegion(e.target.value); setAuctionState(""); }} data-testid="region-filter"
                className="px-3 py-2 bg-[#065F46] border border-[#0F766E] text-white font-sans text-sm focus:outline-none focus:border-[#DC2626]">
                <option value="">All Regions</option>
                {usRegions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <select value={auctionState} onChange={(e) => setAuctionState(e.target.value)} data-testid="state-filter"
                className="px-3 py-2 bg-[#065F46] border border-[#0F766E] text-white font-sans text-sm focus:outline-none focus:border-[#DC2626]">
                <option value="">All States</option>
                {getStatesInRegion(auctionRegion).map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
              </select>
              <div className="flex items-center gap-2">
                <ArrowsDownUp size={18} className="text-[#DC2626]" />
                <select value={auctionSortBy} onChange={(e) => setAuctionSortBy(e.target.value)} data-testid="sort-filter"
                  className="px-3 py-2 bg-[#065F46] border border-[#0F766E] text-white font-sans text-sm focus:outline-none focus:border-[#DC2626]">
                  <option value="price_desc">Highest Price</option>
                  <option value="price_asc">Lowest Price</option>
                  <option value="name">Name (A-Z)</option>
                </select>
              </div>
              <div className="relative flex-1 min-w-[200px]">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
                <input type="text" placeholder="Search auctions..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="auction-search-input" className="w-full pl-9 pr-4 py-2 bg-[#065F46] border border-[#0F766E] text-white placeholder-white/50 font-sans text-sm focus:outline-none focus:border-[#DC2626]" />
              </div>
            </div>

            <div className="bg-white border border-[#E0D4C8] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="auctions-table">
                  <thead className="bg-[#F6E9DD]">
                    <tr>
                      <th className="text-center px-3 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661] w-12">#</th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">Auction</th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">County</th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">State</th>
                      <th className="text-center px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">Day</th>
                      <th className="text-right px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">Price</th>
                      <th className="text-right px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">15min</th>
                      <th className="text-center px-2 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {auctionsLoading ? (
                      <tr><td colSpan={8} className="px-4 py-12 text-center">
                        <CircleNotch size={32} className="animate-spin text-[#DC2626] mx-auto mb-2" />
                        <span className="text-[#6B6661] text-sm">Loading auction prices...</span>
                      </td></tr>
                    ) : filteredAuctions.length === 0 ? (
                      <tr><td colSpan={8} className="px-4 py-12 text-center text-[#6B6661]">No auctions found.</td></tr>
                    ) : filteredAuctions.map((auction, idx) => (
                      <tr key={auction.id} className="hover:bg-[#F9FAFB] transition-colors" data-testid={`auction-row-${idx}`}>
                        <td className="px-3 py-3 text-center">{getRankBadge(idx)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Storefront size={16} className="text-[#DC2626]" />
                            <span className="text-[#33302E] text-sm">{auction.auction_name}</span>
                            {isInWatchlist(auction.auction_name, auctionCategory) && <PushPin size={14} className="text-[#F59E0B]" weight="fill" />}
                          </div>
                        </td>
                        <td className="px-4 py-3"><span className="text-[#6B6661] text-sm">{auction.county}</span></td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 bg-[#E5E7EB] text-xs font-mono ${getRegionColor(auction.region)}`}>{auction.state}</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-[#6B6661] text-xs">{auction.market_day}</span></td>
                        <td className="px-4 py-3 text-right"><span className="font-mono text-sm text-[#33302E]">{formatPrice(auction.price, auction.unit)}</span></td>
                        <td className="px-4 py-3 text-right">
                          <div className={`inline-flex items-center gap-1 px-2 py-0.5 ${getChangeBg(auction.change)}`}>
                            {auction.change > 0 ? <CaretUp size={12} className="text-[#059669]" weight="fill" /> : auction.change < 0 ? <CaretDown size={12} className="text-[#DC2626]" weight="fill" /> : <Minus size={12} className="text-[#6B6661]" />}
                            <span className={`font-mono text-sm ${getChangeColor(auction.change_percent)}`}>{auction.change_percent >= 0 ? "+" : ""}{auction.change_percent.toFixed(1)}%</span>
                          </div>
                        </td>
                        <td className="px-2 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => setHistoryModal({ open: true, market: auction.auction_name, category: auctionCategory, price: auction.price })}
                              className="p-1.5 hover:bg-[#E5E7EB] transition-colors" title="View History">
                              <ChartLine size={16} className="text-[#0F766E]" />
                            </button>
                            <button onClick={() => setAlertModal({ open: true, market: auction.auction_name, price: auction.price, category: auctionCategory, marketType: "auction" })}
                              className="p-1.5 hover:bg-[#E5E7EB] transition-colors" title="Set Alert">
                              <Bell size={16} className="text-[#F59E0B]" />
                            </button>
                            <button onClick={() => toggleWatchlist(auction.auction_name, "auction", auctionCategory)}
                              className="p-1.5 hover:bg-[#E5E7EB] transition-colors" title="Add to Watchlist">
                              <PushPin size={16} className={isInWatchlist(auction.auction_name, auctionCategory) ? "text-[#F59E0B]" : "text-[#6B6661]"} weight={isInWatchlist(auction.auction_name, auctionCategory) ? "fill" : "regular"} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {auctionStats && (
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-[#E0D4C8] p-4">
                  <span className="text-xs font-mono text-[#6B6661] uppercase">Highest</span>
                  <p className="font-mono text-xl text-[#059669] mt-1">{formatPrice(auctionStats.highest, currentCategory?.unit)}</p>
                </div>
                <div className="bg-white border border-[#E0D4C8] p-4">
                  <span className="text-xs font-mono text-[#6B6661] uppercase">Average</span>
                  <p className="font-mono text-xl text-[#33302E] mt-1">{formatPrice(auctionStats.average, currentCategory?.unit)}</p>
                </div>
                <div className="bg-white border border-[#E0D4C8] p-4">
                  <span className="text-xs font-mono text-[#6B6661] uppercase">Lowest</span>
                  <p className="font-mono text-xl text-[#DC2626] mt-1">{formatPrice(auctionStats.lowest, currentCategory?.unit)}</p>
                </div>
                <div className="bg-white border border-[#E0D4C8] p-4">
                  <span className="text-xs font-mono text-[#6B6661] uppercase">Total</span>
                  <p className="font-mono text-xl text-[#33302E] mt-1">{auctionStats.total_auctions}</p>
                </div>
              </div>
            )}
          </>
        )}

        {lastUpdated && (
          <div className="mt-6 text-center">
            <span className="text-xs text-[#6B6661]">Last updated: {new Date(lastUpdated).toLocaleString()} - Prices update every 15 minutes</span>
          </div>
        )}
      </main>
      <Footer />

      <GlobalPriceHistoryModal
        isOpen={historyModal.open}
        onClose={() => setHistoryModal({ open: false, market: null, category: null, price: null })}
        country="usa"
        marketName={historyModal.market}
        category={historyModal.category}
        currentPrice={historyModal.price}
        currency="USD"
        unit="$/cwt"
      />

      <GlobalAlertModal
        isOpen={alertModal.open}
        onClose={() => setAlertModal({ open: false, market: null, price: null, category: null })}
        country="usa"
        marketType={alertModal.marketType}
        marketName={alertModal.market}
        category={alertModal.category}
        currentPrice={alertModal.price}
        currency="USD"
        unit="$/cwt"
        token={token}
      />
    </div>
  );
}
